<script setup lang="ts">
  import {VueFinalModal} from "vue-final-modal";

  const props = defineProps({
    showCloseIcon: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null
    },
    controlsClass: {
      type: String,
      default: 'wad4-modal__controls'
    },
    clickToClose: {
      type: Boolean,
      default: true,
    }
  });

  const emit = defineEmits();
  const slots = useSlots();

  // The sheet is the focus trap's fallback. vue-final-modal traps focus inside the modal, and
  // focus-trap throws when it finds no tabbable element at activation: a modal without the close
  // icon whose controls render late or arrive disabled. That throw happens in a requestAnimationFrame
  // after the open, so it is uncaught and lands in Bugsnag on every such open. tabindex="-1" makes
  // the sheet focusable without adding it to the tab order, so focus rests there until a real
  // control appears.
  const sheet = ref<HTMLElement | null>(null);

  const options = ref({
    modelValue: false,
    teleportTo: 'body',
    displayDirective: 'if',
    hideOverlay: false,
    overlayTransition: 'vfm-fade',
    contentTransition: 'wad4-modal-slide',
    contentClass: 'wad4-modal-vfm',
    clickToClose: props.clickToClose,
    escToClose: false,
    background: 'non-interactive',
    lockScroll: true,
    reserveScrollBarGap: true,
    swipeToClose: 'none',
    focusTrap: {
      // vue-final-modal's own default; passing an object replaces it, so it has to be restated.
      allowOutsideClick: true,
      fallbackFocus: () => sheet.value as HTMLElement,
    },
  });

  const closeModal = () => {
    options.value.modelValue = false;
    emit('update:modelValue', false);
  };

</script>

<template>
  <VueFinalModal v-model="options.modelValue" v-bind="options">
    <div ref="sheet" class="wad4-modal" tabindex="-1">
        <header class="wad4-modal__topbar">
          <div class="wad4-modal__topbar-inner">
            <slot name="title">
              <h2 v-if="title" class="wad4-modal__title">{{title}}</h2>
              <span v-else></span>
            </slot>
            <button v-if="props.showCloseIcon" type="button" class="wad4-modal__close" @click="closeModal" aria-label="Close">
              <svg width="24" height="24" viewBox="-5.25 -5.25 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M12.2197 0.21967C12.5126 -0.0732233 12.9873 -0.0732233 13.2802 0.21967C13.5731 0.512563 13.5731 0.987324 13.2802 1.28022L7.81049 6.74994L13.2802 12.2197C13.5731 12.5126 13.5731 12.9873 13.2802 13.2802C12.9873 13.5731 12.5126 13.5731 12.2197 13.2802L6.74994 7.81049L1.28022 13.2802C0.987324 13.5731 0.512563 13.5731 0.21967 13.2802C-0.0732233 12.9873 -0.0732233 12.5126 0.21967 12.2197L5.6894 6.74994L0.21967 1.28022C-0.0732233 0.987324 -0.0732233 0.512563 0.21967 0.21967C0.512563 -0.0732233 0.987324 -0.0732233 1.28022 0.21967L6.74994 5.6894L12.2197 0.21967Z"/>
              </svg>
            </button>
          </div>
        </header>
        <section class="wad4-modal__content">
          <slot name="content"></slot>
          <div v-if="slots.controls && slots.controls().length > 0" :class="controlsClass">
            <slot name="controls"></slot>
          </div>
        </section>
    </div>
  </VueFinalModal>
</template>

<style lang="scss">
.wad4-modal {
  display: flex;
  flex-direction: column;
  background-color: $wad4-c-background-bg-primary;
  // Stay interactive while the full-screen content wrapper lets outside clicks fall through
  // to the vfm root (so clickToClose can close the modal on click outside the sheet).
  pointer-events: auto;
  // Teleported to <body>, outside the .wad4 wrapper — set a themed base text colour so slotted
  // content (paragraphs, etc.) doesn't fall back to the UA default (black) and vanish on dark.
  color: $wad4-c-text-text-primary;
  width: 100%;
  height: auto;
  max-height: 100%;
  max-width: 900px;
  overflow: hidden;

  // Programmatic fallback focus (see the script) must not draw a ring around the whole sheet.
  &:focus {
    outline: none;
  }

  // Top bar: app-header-style row with the close icon right-aligned and a bottom divider.
  &__topbar {
    position: sticky;
    top: 0;
    z-index: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 60px;
    padding: 0 $wad4-container-padding;
    background: $wad4-c-background-bg-primary;
    border-bottom: 1px solid $wad4-c-border-border-subtle;

    &-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $wad4-spacing-lg;
      width: 100%;
      max-width: 900px;
      margin: 0 auto;
    }
  }

  &__title {
    @include text-size-mixin('lg');
    font-weight: $wad4-font-weight-semi-bold;
    color: $wad4-c-text-text-primary;
    margin: 0;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: $wad4-spacing-md;
    border: none;
    background: none;
    border-radius: $wad4-border-radius-md;
    color: $wad4-c-foreground-fg-primary;
    cursor: pointer;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }
  }

  &__content {
    flex: 1 1 auto;
    width: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-3xl;
    // Sides + top use the container padding; the bottom uses the section spacing (40px,
    // = Figma viewport-section-spacing-y) so the action button breathes at the bottom.
    padding: $wad4-container-padding;
    padding-bottom: calc(#{$wad4-spacing-5xl} + env(safe-area-inset-bottom, 0px));
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__controls {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: $wad4-spacing-lg;
    align-self: stretch;
  }
}

// VFM content element — full-screen flex container that bottom-anchors the sheet.
// (Being full-height is what lets the slide transform actually move it.)
.wad4-modal-vfm {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  // The wrapper spans the viewport but must not swallow clicks on the empty area above the
  // sheet — let them fall through to the vfm root so clickToClose fires (close on click outside).
  pointer-events: none;
  // Keep a full-height sheet below the iOS notch (standalone PWA); short sheets sit at the
  // bottom unaffected. The sheet's own top bar no longer carries this inset.
  padding-top: env(safe-area-inset-top, 0px);
}

// Modal slides up from the bottom (backdrop fades via vfm-fade).
.wad4-modal-slide-enter-active,
.wad4-modal-slide-leave-active {
  transition: transform 0.3s ease;
}
.wad4-modal-slide-enter-from,
.wad4-modal-slide-leave-to {
  transform: translateY(100%);
}
</style>
